import { useFormatLocalizedNumber } from '@pba/react-utils';
import { Table as TableA } from 'antd';
import type { ColumnsType as ColumnsTypeA, TableProps } from 'antd/lib/table';
import type { SorterResult as SorterResultA } from 'antd/lib/table/interface';
import cn from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Pagination } from '../../types';
import { Flex } from '../flex';
import { resolveClassNames } from '../utils';
import { EmptyState } from './empty-state';
import { type SorterMap, useOnTableChange } from './hooks';
import { IconTitle } from './icon-title';
import styles from './table.module.css';

export type ColumnsType<T> = ColumnsTypeA<T>;
export type SorterResult<T> = SorterResultA<T>;

type P<T, TSorter extends SorterMap> = {
  clickableRows?: ((record: T) => boolean) | boolean;
  emptyState?: ReactNode;
  header?: {
    extra?: ReactNode;
    icon: ReactNode;
    title: string;
  };
  updateSearchParams?: (params: { pagination?: Partial<Pagination>; sorter?: TSorter }) => void;
} & TableProps<T>;

const TableComponent = <RecordType extends object, TSorter extends SorterMap = SorterMap>({
  className,
  classNames,
  clickableRows,
  dataSource,
  emptyState,
  header,
  loading,
  locale,
  onChange,
  onRow,
  rowClassName,
  rowSelection,
  updateSearchParams,
  ...rest
}: P<RecordType, TSorter>) => {
  const { t } = useTranslation(['translationReactComponents']);
  const formatLocalizedNumber = useFormatLocalizedNumber();
  const onTableChange = useOnTableChange<RecordType, TSorter>({ updateSearchParams });
  const isClickableRow = (record: RecordType) => {
    if (clickableRows !== undefined) {
      return typeof clickableRows === 'function' ? clickableRows(record) : clickableRows;
    }

    return onRow !== undefined;
  };
  const selectionColumnClassName = styles['selection-column'];
  const resolvedLoading = typeof loading === 'object' ? loading : { spinning: Boolean(loading) };
  const loadingClassNames = typeof resolvedLoading.classNames === 'object' ? resolvedLoading.classNames : undefined;

  return (
    <>
      {header ? (
        <Flex align="center" className={styles.header} justify="space-between">
          <IconTitle icon={header.icon} title={header.title} />
          {header.extra}
        </Flex>
      ) : null}
      <TableA
        bordered
        className={cn(className, { [styles.empty ?? '']: !dataSource?.length })}
        classNames={resolveClassNames(classNames, (resolvedClassNames) => ({
          ...resolvedClassNames,
          body: {
            ...resolvedClassNames?.body,
            cell: cn(styles['body-cell'], resolvedClassNames?.body?.cell),
            row: cn(styles['body-row'], resolvedClassNames?.body?.row),
          },
          content: cn(styles.content, resolvedClassNames?.content),
          pagination: {
            ...resolvedClassNames?.pagination,
            root: cn(styles.pagination, resolvedClassNames?.pagination?.root),
          },
          root: cn(styles.table, resolvedClassNames?.root),
          section: cn(styles.container, resolvedClassNames?.section),
        }))}
        dataSource={dataSource}
        loading={{
          ...resolvedLoading,
          classNames: {
            ...loadingClassNames,
            container: cn(styles['spin-container'], loadingClassNames?.container),
            root: cn(styles['spin-root'], loadingClassNames?.root),
          },
        }}
        locale={{
          ...locale,
          emptyText: emptyState,
        }}
        onChange={(tablePagination, tableFilter, tableSorter, extra) => {
          onTableChange(tablePagination, tableFilter, tableSorter, extra);
          onChange?.(tablePagination, tableFilter, tableSorter, extra);
        }}
        onRow={
          onRow
            ? (record, index) => {
                const rowProperties = onRow(record, index);

                return {
                  ...rowProperties,
                  onClick(event: MouseEvent<HTMLElement>) {
                    // Column with checkboxes is not clickable
                    if (
                      event.target instanceof HTMLElement &&
                      selectionColumnClassName &&
                      event.target.closest(`.${selectionColumnClassName}`)
                    ) {
                      return;
                    }

                    rowProperties.onClick?.(event);
                  },
                };
              }
            : undefined
        }
        rowClassName={(value, index, intent) =>
          cn(typeof rowClassName === 'string' ? rowClassName : rowClassName?.(value, index, intent), {
            [styles['row-clickable'] ?? '']: isClickableRow(value),
          })
        }
        rowKey="id"
        {...rest}
        pagination={
          rest.pagination === false
            ? false
            : {
                placement: ['bottomStart'],
                showTotal: (total, range) =>
                  t('table.pagination', {
                    count: total,
                    from: formatLocalizedNumber({ value: range[0] }),
                    to: formatLocalizedNumber({ value: range[1] }),
                    total: formatLocalizedNumber({ value: total }),
                  }),
                ...rest.pagination,
              }
        }
        rowSelection={
          rowSelection
            ? {
                ...rowSelection,
                onCell(record, index) {
                  const cellProperties = rowSelection.onCell?.(record, index);

                  return {
                    ...cellProperties,
                    className: cn(selectionColumnClassName, cellProperties?.className),
                  };
                },
              }
            : undefined
        }
      />
    </>
  );
};

const Table = Object.assign(TableComponent, { Empty: EmptyState });

export default Table;

export { type FilterValue } from 'antd/lib/table/interface';
